import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Card, CardBody, Col, Container, Row, Input, Button, Badge } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

import BookingsHistory from './bookingsTable';

// Import Images
import avatar3 from "../../assets/images/users/avatar-3.jpg";

import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';

const CustomerDetails = props => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [customerDetails, setCustomerDetails] = useState(null);
    const [customerBookings, setCustomerBookings] = useState([]);
    const [editingName, setEditingName] = useState(false);
    const [editingEmail, setEditingEmail] = useState(false);
    const [nameValue, setNameValue] = useState('');
    const [emailValue, setEmailValue] = useState('');

    useEffect(() => {
        if (id) {
            fetchCustomerDetails();
            fetchCustomerBookings();
        }
    }, [id]);

    const fetchCustomerDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_WEB_APP_URL}/api/web/customers/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            if (response.data.success) {
                setCustomerDetails(response.data.data);
                setNameValue(response.data.data.user_name || '');
                setEmailValue(response.data.data.email || '');
            } else {
                toast.error("Failed to fetch customer details");
                navigate("/customers");
            }
        } catch (error) {
            toast.error("Failed to fetch customer details");
            console.error("Error fetching customer details:", error);
            navigate("/customers");
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomerBookings = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const bookingsResponse = await axios.get(
                `${process.env.REACT_APP_WEB_APP_URL}/api/web/bookings`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );
            
            if (bookingsResponse.data.success) {
                const allBookings = bookingsResponse.data.data || [];
                // Filter bookings by customer ID - API returns { booking: {...}, customer: {...}, vehicle_type: {...} }
                const filteredBookings = allBookings.filter(item => {
                    const booking = item.booking;
                    const customer = item.customer;
                    if (!booking) return false;
                    
                    return customer?.id === parseInt(id) || 
                           customer?.id === id ||
                           booking.customer_id === parseInt(id) ||
                           booking.customer_id === id;
                });
                setCustomerBookings(filteredBookings);
            }
        } catch (error) {
            console.error("Error fetching customer bookings:", error);
        }
    };

    const handleUpdateName = async () => {
        try {
            const token = localStorage.getItem("authToken");
            // Split name into first_name and last_name
            const nameParts = nameValue.trim().split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';
            
            const response = await axios.put(
                `${process.env.REACT_APP_WEB_APP_URL}/api/web/customers/update`,
                {
                    id: parseInt(id),
                    first_name: firstName,
                    last_name: lastName,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            if (response.data.success) {
                toast.success("Name updated successfully");
                setEditingName(false);
                fetchCustomerDetails();
            } else {
                toast.error("Failed to update name");
            }
        } catch (error) {
            toast.error("Failed to update name");
            console.error("Error updating name:", error);
        }
    };

    const handleUpdateEmail = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.put(
                `${process.env.REACT_APP_WEB_APP_URL}/api/web/customers/update`,
                {
                    id: parseInt(id),
                    email: emailValue,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            
            if (response.data.success) {
                toast.success("Email updated successfully");
                setEditingEmail(false);
                fetchCustomerDetails();
            } else {
                toast.error("Failed to update email");
            }
        } catch (error) {
            toast.error("Failed to update email");
            console.error("Error updating email:", error);
        }
    };

    const handleBlockUnblock = async (action) => {
        Swal.fire({
            title: `Are you sure you want to ${
                action === "disable" ? "block" : "unblock"
            } this customer?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#E53935",
            cancelButtonColor: "#3085d6",
            confirmButtonText: `Yes, ${action === "disable" ? "block" : "unblock"} it!`,
        }).then(async result => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("authToken");
                    const response = await axios.post(
                        `${process.env.REACT_APP_WEB_APP_URL}/api/web/users/${action}/${id}`,
                        {},
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                    if (response.status == 200) {
                        toast.success(
                            `Customer successfully ${
                                action == "disable" ? "blocked" : "unblocked"
                            }.`
                        );
                        fetchCustomerDetails();
                    } else {
                        toast.error(
                            `Failed to ${action == "disable" ? "block" : "unblock"} customer`
                        );
                    }
                } catch (error) {
                    if (error.response?.status == 409) {
                        return Swal.fire({
                            title: `The customer has ${error?.response?.data?.data?.active_bookings_count} active bookings, blocking will cancel all these bookings. Confirm block the customer?`,
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#E53935",
                            cancelButtonColor: "#3085d6",
                            confirmButtonText: `Yes, block it!`,
                        }).then(async result => {
                            if (result.isConfirmed) {
                                try {
                                    const formData = new FormData();
                                    formData.append("force", 1);
                                    const token = localStorage.getItem('authToken');
                                    const response2 = await axios.post(
                                        `${process.env.REACT_APP_WEB_APP_URL}/api/web/users/${action}/${id}`,
                                        formData,
                                        {
                                            headers: {
                                                Accept: 'application/json',
                                                Authorization: `Bearer ${token}`,
                                                "Content-Type": "multipart/form-data",
                                            },
                                        }
                                    );
                                    if (response2.status == 200) {
                                        toast.success(response2.data.message || "Customer blocked successfully");
                                        fetchCustomerDetails();
                                    } else {
                                        toast.error(`Failed to block customer.`);
                                    }
                                } catch (error) {
                                    toast.error(error.message || `Failed to block customer.`);
                                }
                            }
                        });
                    }
                    toast.error(error.message || `Failed to ${action == "disable" ? "block" : "unblock"} customer`);
                }
            }
        });
    };

    // Calculate booking statistics
    const bookingStats = useMemo(() => {
        if (!customerBookings || customerBookings.length === 0) {
            return { all: 0, completed: 0, cancelled: 0 };
        }
        
        const all = customerBookings.length;
        const completed = customerBookings.filter(item => {
            const status = (item.booking?.status || '').toLowerCase();
            return status === 'complete' || status === 'completed' || status === 'done';
        }).length;
        const cancelled = customerBookings.filter(item => {
            const status = (item.booking?.status || '').toLowerCase();
            return status === 'cancel' || status === 'cancelled';
        }).length;
        
        return { all, completed, cancelled };
    }, [customerBookings]);

    const isActive = customerDetails?.is_active == 1;

    document.title = "Customer Details | Moeen";
    return (
        <React.Fragment>
            <div className="page-content pt-2">
                <Container fluid>
                    <Breadcrumbs maintitle="Home" title="Customers" breadcrumbItem="Customer Details" />

                    <Row>
                        <Col xl={3}>
                            <div className="user-sidebar">
                                <Card>
                                    <CardBody>
                                        <div className="mt-n4 position-relative">
                                            <div className="text-center">
                                                {loading ? (
                                                    <Skeleton circle height={80} width={80} />
                                                ) : (
                                                    <img 
                                                        src={customerDetails?.profile_pic || avatar3} 
                                                        alt="" 
                                                        className="avatar-xl rounded-circle img-thumbnail" 
                                                    />
                                                )}

                                                <div className="mt-3">
                                                    <h5 className="">
                                                        {loading ? <Skeleton width={100} /> : customerDetails?.user_name}
                                                    </h5>
                                                    <div>
                                                        {loading ? <Skeleton width={120} /> : (
                                                            <span className="text-muted m-1">
                                                                {customerDetails?.phone_code || ''} {customerDetails?.mobno || ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-3 mt-3">
                                            <Row className="text-center">
                                                <Col className="border-end">
                                                    <div className="p-1">
                                                        <h5 className="mb-1">{loading ? <Skeleton width={10} /> : bookingStats.all}</h5>
                                                        <p className="text-muted mb-0">Total Bookings</p>
                                                    </div>
                                                </Col>
                                                <Col>
                                                    <div className="p-1">
                                                        <h5 className="mb-1">{loading ? <Skeleton width={10} /> : bookingStats.completed}</h5>
                                                        <p className="text-muted mb-0">Completed</p>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </CardBody>
                                </Card>

                                <Card>
                                    <div className="card-header">
                                        <h4 className="card-title">Personal Information</h4>
                                    </div>
                                    <CardBody>
                                        {loading ? (
                                            <ul className="list-unstyled mb-0">
                                                {[...Array(4)].map((_, index) => (
                                                    <li
                                                        key={index}
                                                        className="d-flex justify-content-between p-2 font-size-15"
                                                    >
                                                        <Skeleton width={200} />
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <ul className="list-unstyled mb-0">
                                                <li className="d-flex justify-content-between align-items-center p-2 font-size-15">
                                                    <span className="text-muted">Name</span>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {editingName ? (
                                                            <>
                                                                <Input
                                                                    type="text"
                                                                    value={nameValue}
                                                                    onChange={(e) => setNameValue(e.target.value)}
                                                                    className="form-control form-control-sm"
                                                                    style={{ width: '150px' }}
                                                                />
                                                                <Button
                                                                    color="success"
                                                                    size="sm"
                                                                    onClick={handleUpdateName}
                                                                >
                                                                    <i className="mdi mdi-check"></i>
                                                                </Button>
                                                                <Button
                                                                    color="secondary"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setEditingName(false);
                                                                        setNameValue(customerDetails?.user_name || '');
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-close"></i>
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span>{customerDetails?.user_name || 'N/A'}</span>
                                                                <Button
                                                                    color="link"
                                                                    size="sm"
                                                                    onClick={() => setEditingName(true)}
                                                                    className="p-0"
                                                                >
                                                                    <i className="mdi mdi-pencil"></i>
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                </li>
                                                <li className="d-flex justify-content-between align-items-center p-2 font-size-15">
                                                    <span className="text-muted">Email</span>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {editingEmail ? (
                                                            <>
                                                                <Input
                                                                    type="email"
                                                                    value={emailValue}
                                                                    onChange={(e) => setEmailValue(e.target.value)}
                                                                    className="form-control form-control-sm"
                                                                    style={{ width: '150px' }}
                                                                />
                                                                <Button
                                                                    color="success"
                                                                    size="sm"
                                                                    onClick={handleUpdateEmail}
                                                                >
                                                                    <i className="mdi mdi-check"></i>
                                                                </Button>
                                                                <Button
                                                                    color="secondary"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setEditingEmail(false);
                                                                        setEmailValue(customerDetails?.email || '');
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-close"></i>
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span>{customerDetails?.email || 'N/A'}</span>
                                                                <Button
                                                                    color="link"
                                                                    size="sm"
                                                                    onClick={() => setEditingEmail(true)}
                                                                    className="p-0"
                                                                >
                                                                    <i className="mdi mdi-pencil"></i>
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                </li>
                                                <li className="d-flex justify-content-between p-2 font-size-15">
                                                    <span className="text-muted">Gender</span>
                                                    <span>{(() => {
                                                        const gender = customerDetails?.gender;
                                                        if (gender === 1 || gender === '1' || gender === true) return 'Male';
                                                        if (gender === 0 || gender === '0' || gender === false) return 'Female';
                                                        return 'N/A';
                                                    })()}</span>
                                                </li>
                                                <li className="d-flex justify-content-between p-2 font-size-15">
                                                    <span className="text-muted">Status</span>
                                                    <Badge color={isActive ? "success" : "warning"}>
                                                        {isActive ? "Active" : "Blocked"}
                                                    </Badge>
                                                </li>
                                            </ul>
                                        )}
                                    </CardBody>
                                </Card>

                                <Card>
                                    <CardBody>
                                        <div className="d-grid gap-2">
                                            {isActive ? (
                                                <Button
                                                    color="danger"
                                                    onClick={() => handleBlockUnblock("disable")}
                                                >
                                                    <i className="mdi mdi-block-helper me-1"></i> Block Customer
                                                </Button>
                                            ) : (
                                                <Button
                                                    color="warning"
                                                    onClick={() => handleBlockUnblock("enable")}
                                                >
                                                    <i className="mdi mdi-check-circle-outline me-1"></i> Unblock Customer
                                                </Button>
                                            )}
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        </Col>

                        <Col xl={9}>
                            {/* Analytics Cards */}
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Card>
                                        <CardBody>
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0 me-3">
                                                    <div className="avatar-xs">
                                                        <div className="avatar-title rounded-circle bg-primary bg-gradient fs-4">
                                                            <i className="mdi mdi-calendar-check"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <p className="text-muted mb-1">Total Bookings</p>
                                                    <h5 className="mb-0">{loading ? <Skeleton width={50} /> : bookingStats.all}</h5>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card>
                                        <CardBody>
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0 me-3">
                                                    <div className="avatar-xs">
                                                        <div className="avatar-title rounded-circle bg-success bg-gradient fs-4">
                                                            <i className="mdi mdi-check-circle"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <p className="text-muted mb-1">Completed Bookings</p>
                                                    <h5 className="mb-0">{loading ? <Skeleton width={50} /> : bookingStats.completed}</h5>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card>
                                        <CardBody>
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0 me-3">
                                                    <div className="avatar-xs">
                                                        <div className="avatar-title rounded-circle bg-warning bg-gradient fs-4">
                                                            <i className="mdi mdi-cancel"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <p className="text-muted mb-1">Cancelled Bookings</p>
                                                    <h5 className="mb-0">{loading ? <Skeleton width={50} /> : bookingStats.cancelled}</h5>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Bookings History */}
                            <Card>
                                <div className="p-4 pb-0">
                                    <h5 className="card-title mb-0">Bookings History</h5>
                                </div>
                                <CardBody>
                                    <BookingsHistory customerId={id} />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default CustomerDetails;

